import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PRINTER_NUMBER_MAX_LENGTH } from '../constants';
import { RootState } from '../redux/reducers/rootReducer';
import {
  getMobilePrinterListFeatureEnabled,
  getMobilePrinterNumber,
  isMobilePrinterConnected,
  removeMobilePrinterFromStorage,
  saveMobilePrinterToStorage,
  getMobilePrinterTestConnectionErrorMessage,
} from '../utils/mobilePrinter';

import {
  getMobilePrinterList,
  resetMobilePrinter,
  testMobilePrinterConnection,
  resetTestMobilePrinterConnection,
} from '../redux/actions/mobilePrinter.actions';

interface UseMobilePrinterOptions {
  shouldFetchPrinterList: boolean;
  shouldShowToast?: boolean;
}

export const useMobilePrinter = ({ shouldFetchPrinterList, shouldShowToast = false }: UseMobilePrinterOptions) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { storeID } = useSelector((state: RootState) => state.pickerStore);
  const mobilePrinterList = useSelector((state: RootState) => state.mobilePrinter.printerList.printers);
  const mobilePrinterListError = useSelector((state: RootState) => state.mobilePrinter.printerList.error);
  const mobilePrinterListLoading = useSelector((state: RootState) => state.mobilePrinter.printerList.isLoading);
  const testConnectionStatus = useSelector((state: RootState) => state.mobilePrinter.testConnection.status);
  const testConnectionErrorCode = useSelector((state: RootState) => state.mobilePrinter.testConnection.errorCode);
  const testConnectionIsLoading = useSelector((state: RootState) => state.mobilePrinter.testConnection.isLoading);

  const translationRoot = 'MOBILE_PRINTER';

  const testConnectionMessage =
    testConnectionStatus === 'error' || testConnectionStatus === 'warning'
      ? getMobilePrinterTestConnectionErrorMessage(testConnectionErrorCode, t, translationRoot)
      : null;

  const hasFetchedRef = useRef(false);
  // Local state
  const [manualPrinterNumber, setManualPrinterNumber] = useState('');
  const [selectedPrinterFromList, setSelectedPrinterFromList] = useState('');
  const [isPrinterConnected, setIsPrinterConnected] = useState(isMobilePrinterConnected());
  const [connectedPrinterNumber, setConnectedPrinterNumber] = useState(getMobilePrinterNumber());

  const printerToConnect = manualPrinterNumber.trim() || selectedPrinterFromList;
  const mobilePrinterListFeatureEnabled = getMobilePrinterListFeatureEnabled();

  // Fetch printer list
  useEffect(() => {
    if (shouldFetchPrinterList && !hasFetchedRef.current && !isPrinterConnected) {
      if (mobilePrinterListFeatureEnabled) {
        dispatch(resetTestMobilePrinterConnection());
      }
      dispatch(getMobilePrinterList({ storeId: storeID }));
      hasFetchedRef.current = true;
    }

    return () => {
      hasFetchedRef.current = false;
    };
  }, [isPrinterConnected, dispatch, shouldFetchPrinterList, mobilePrinterListFeatureEnabled, storeID]);

  const shouldDisableConnectButton =
    (!isPrinterConnected && !manualPrinterNumber && !selectedPrinterFromList) || testConnectionStatus === 'error';

  const handleManualPrinterNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(/[^0-9]/g, '').slice(0, PRINTER_NUMBER_MAX_LENGTH);
      setManualPrinterNumber(inputValue);
      setSelectedPrinterFromList('');
      if (mobilePrinterListFeatureEnabled && testConnectionStatus !== null) {
        dispatch(resetTestMobilePrinterConnection());
      }
    },
    [dispatch, testConnectionStatus, mobilePrinterListFeatureEnabled]
  );

  const handlePrinterSelectionFromList = useCallback(
    (printer: string) => {
      setSelectedPrinterFromList(printer);
      setManualPrinterNumber('');
      if (testConnectionStatus !== null) {
        dispatch(resetTestMobilePrinterConnection());
      }
    },
    [dispatch, testConnectionStatus]
  );

  const disconnectPrinter = useCallback(() => {
    setManualPrinterNumber('');
    setSelectedPrinterFromList('');
    setIsPrinterConnected(false);
    setConnectedPrinterNumber('');
    removeMobilePrinterFromStorage();
    dispatch(resetMobilePrinter());
  }, [dispatch]);

  const skipPrinterConnection = useCallback(() => {
    saveMobilePrinterToStorage('');
    if (mobilePrinterListFeatureEnabled) {
      dispatch(resetTestMobilePrinterConnection());
    }
  }, [dispatch, mobilePrinterListFeatureEnabled]);

  const completePrinterConnection = useCallback(() => {
    setIsPrinterConnected(true);
    setConnectedPrinterNumber(printerToConnect);
    saveMobilePrinterToStorage(printerToConnect);
    if (mobilePrinterListFeatureEnabled) {
      dispatch(resetTestMobilePrinterConnection());
    }
  }, [dispatch, printerToConnect, mobilePrinterListFeatureEnabled]);

  const connectPrinter = useCallback(() => {
    if (mobilePrinterListFeatureEnabled) {
      dispatch(testMobilePrinterConnection({ printerNumber: printerToConnect, displayToast: shouldShowToast }));
    } else {
      completePrinterConnection();
    }
  }, [dispatch, printerToConnect, shouldShowToast, mobilePrinterListFeatureEnabled, completePrinterConnection]);

  const handlePrinterConnectionToggle = useCallback(() => {
    if (isPrinterConnected) {
      disconnectPrinter();
    } else {
      connectPrinter();
    }
  }, [isPrinterConnected, connectPrinter, disconnectPrinter]);

  const resetTestPrinterConnection = useCallback(() => {
    dispatch(resetTestMobilePrinterConnection());
    setManualPrinterNumber('');
    setSelectedPrinterFromList('');
  }, [dispatch]);

  const resetToLocalStorageState = useCallback(() => {
    const printerNumber = getMobilePrinterNumber();
    const isConnected = isMobilePrinterConnected();
    setConnectedPrinterNumber(printerNumber);
    setIsPrinterConnected(isConnected);
    if (mobilePrinterListFeatureEnabled) {
      resetTestPrinterConnection();
    }
  }, [resetTestPrinterConnection, mobilePrinterListFeatureEnabled]);

  return {
    shouldDisableConnectButton,
    // State
    manualPrinterNumber,
    mobilePrinterList,
    mobilePrinterListError,
    mobilePrinterListLoading,
    selectedPrinterFromList,
    isPrinterConnected,
    connectedPrinterNumber,

    // Handlers
    handleManualPrinterNumberChange,
    handlePrinterConnectionToggle,
    handlePrinterSelectionFromList,
    connectPrinter,
    disconnectPrinter,
    skipPrinterConnection,
    resetToLocalStorageState,
    testConnectionStatus,
    testConnectionMessage,
    testConnectionIsLoading,
    resetTestPrinterConnection,
    completePrinterConnection,
  };
};
